import React, { useRef, useCallback, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { Button, Select, Tooltip, notification } from 'antd'
import * as Icons from '@ant-design/icons'

import { useGlobalShortcut } from 'admin/lib/shortcuts'
import { Matchers } from 'admin/lib/Records'
import { FormRow, FormItem } from 'admin/lib/Forms'
import { Queries } from 'admin/lib/Api'
import { calculateAssignmentOptions } from 'admin/lib/assigneeFunctions'
import { RAW } from './constants'

export function FormControls({
  dataView,
  saving,
  currentStatus,
  previousStatus,
  hasViewedLineItemSummary,
  setHasViewedLineItemSummary,
  setLineItemSummaryOpen,
  lineItemCount,
  hasUnsavedLineItemChanges
}) {
  const { hiddenElement, triggerSubmit } = useSaveShortcut(saving)
  const users = Queries.useQflowUsers()
  const { setValue, formState } = useFormContext()

  const isRawDataView = dataView === RAW

  // Check if any fields are dirty
  const hasDirtyFields = useMemo(() => {
    // Check for unsaved line item changes first
    if (hasUnsavedLineItemChanges) return true

    // Access formState.dirtyFields directly to trigger Proxy subscription
    // Don't destructure at top level or subscription won't work properly
    const dirtyFieldKeys = Object.keys(formState.dirtyFields || {})

    // If there are no dirty fields at all, return false
    if (dirtyFieldKeys.length === 0) return false

    // Helper function to recursively check if an object has any actual dirty values
    const hasActualDirtyValues = obj => {
      if (!obj || typeof obj !== 'object') {
        return true // Primitive values are considered dirty
      }
      const keys = Object.keys(obj)
      if (keys.length === 0) {
        return false // Empty objects are not dirty
      }
      // Recursively check nested objects
      return keys.some(key => hasActualDirtyValues(obj[key]))
    }

    // Check if any of the other dirty keys have actual dirty nested fields
    return dirtyFieldKeys.some(key =>
      hasActualDirtyValues(formState.dirtyFields[key])
    )
  }, [hasUnsavedLineItemChanges, formState])

  const handleStatusChange = value => {
    // Check if status is being changed to Published or Escalated and summary hasn't been viewed
    if (
      ((previousStatus !== 'Published' && value === 'Published') ||
        (previousStatus !== 'Escalated' && value === 'Escalated')) &&
      !hasViewedLineItemSummary &&
      lineItemCount > 1
    ) {
      notification.warning({
        message:
          'Please review the Line Item Summary before publishing/escalating the record.',
        placement: 'top'
      })
      setLineItemSummaryOpen(true)
      setHasViewedLineItemSummary(true)
      return
    }

    setValue('status', value)

    // Trigger form submission after status change
    setTimeout(() => {
      triggerSubmit()
    }, 100)
  }

  const statusOptions = [
    {
      label: isRawDataView ? (
        <Tooltip title="Change view to Enriched to select">Published</Tooltip>
      ) : (
        'Published'
      ),
      value: 'Published',
      disabled: isRawDataView
    },
    { label: 'Escalated', value: 'Escalated', disabled: false },
    { label: 'Failed', value: 'Failed', disabled: false },
    { label: 'Void', value: 'Void', disabled: false },
    { label: 'Removed', value: 'Removed', disabled: true },
    { label: 'New', value: 'New', disabled: true }
  ]

  return (
    <>
      <FormRow>
        <FormItem label="Status" name="status">
          <Tooltip
            title={hasDirtyFields ? 'Save changes before changing status' : ''}
          >
            <Select
              value={currentStatus}
              onChange={handleStatusChange}
              disabled={hasDirtyFields}
              options={statusOptions}
            />
          </Tooltip>
        </FormItem>

        {hiddenElement}

        <Button
          type="primary"
          style={{ width: '37.5%' }}
          htmlType="submit"
          icon={<Icons.UploadOutlined />}
          disabled={saving}
        >
          Save {dataView}
        </Button>
      </FormRow>

      {users.data && (
        <FormRow style={{ width: '100%' }}>
          <FormItem label="Assignee" name="assignee">
            <Select showSearch={true}>
              {calculateAssignmentOptions(users.data)}
            </Select>
          </FormItem>
        </FormRow>
      )}
    </>
  )
}

function useSaveShortcut(saving) {
  const submitButtonRef = useRef()
  const isClickingRef = useRef(false)

  const triggerSubmit = useCallback(() => {
    if (submitButtonRef.current && !submitButtonRef.current.disabled) {
      submitButtonRef.current.click()
    }
  }, [])

  const handleShortcut = useCallback(() => {
    // Immediate synchronous check to prevent double-clicks
    if (isClickingRef.current) {
      return
    }

    isClickingRef.current = true

    try {
      if (document.activeElement?.blur) {
        document.activeElement.blur()
      }
      triggerSubmit()
    } catch (error) {
      console.error('Error triggering save shortcut:', error)
    } finally {
      // Reset the flag after a delay to allow subsequent saves
      setTimeout(() => {
        isClickingRef.current = false
      }, 2000)
    }
  }, [triggerSubmit])

  useGlobalShortcut(Matchers.form.save, handleShortcut)

  return {
    hiddenElement: (
      <input type="submit" hidden ref={submitButtonRef} disabled={saving} />
    ),
    triggerSubmit
  }
}
